import { extendBlockRegistry } from '@/blocks/registry-maps'
import { extendToolMetadata } from '@/tools/metadata'
import { extendToolOutputs } from '@/tools/metadata-outputs'
import { extendToolIds } from '@/tools/tool-ids'
import { extendTriggerRegistry } from '@/triggers/registry'

/**
 * The integration catalogue, loaded on demand.
 *
 * The bundle always carries the executor's own block types and the six
 * providers a brand connects by OAuth (`blocks/registry-core`,
 * `triggers/registry-core`, the core halves of `tools/generated`). The other
 * ~290 blocks — most of which the integration runtime runs — their triggers
 * and their tool tables are chunks this function fetches, because before it
 * existed every brand downloaded 14 MB of definitions to open a chat. The
 * Integrations screens and the workflow editor ask for it before they render,
 * and a workflow that names a block outside the core set asks for it before
 * its document is handed to the editor.
 *
 * Triggers land before blocks: a catalogue block spreads its provider's
 * trigger sub-blocks at module scope, so the trigger registry has to hold
 * them by the time that module evaluates.
 */
let loading: Promise<void> | null = null
let loaded = false

export function loadIntegrationCatalogue(): Promise<void> {
  loading ??= (async () => {
    const triggers = await import('@/triggers/registry-catalogue')
    extendTriggerRegistry(triggers.TRIGGERS_CATALOGUE)
    const [ids, metadata, outputs, blocks] = await Promise.all([
      import('@/tools/generated/tool-ids.catalogue'),
      import('@/tools/generated/tool-metadata.catalogue'),
      import('@/tools/generated/tool-outputs.catalogue'),
      import('@/blocks/registry-catalogue'),
    ])
    extendToolIds(ids.default)
    extendToolMetadata(metadata.default)
    extendToolOutputs(outputs.default)
    extendBlockRegistry(blocks.BLOCKS_CATALOGUE, blocks.BLOCK_META_CATALOGUE)
    loaded = true
  })()
  return loading
}

export function isIntegrationCatalogueLoaded(): boolean {
  return loaded
}
